/** Query params forwarded from a configurator URL to /screenshot. */
export interface ScreenshotUrlOptions {
  /** Override origin (defaults to the input URL origin). */
  baseOrigin?: string
  camera?: string
  shadows?: string
  showOnly?: string
}

export interface FrameApiRenderRequest {
  url: string
  width: number
  height: number
  wait_timeout_ms: number
  device_scale_factor: number
  capture_mode: 'screenshot'
}

export function isScreenshotUrl(url: string): boolean {
  try {
    return new URL(url).pathname.replace(/\/+$/, '') === '/screenshot'
  } catch {
    return false
  }
}

function cleanBedSlug(bed: string): string {
  return bed.split('?')[0].split('/')[0]
}

function readRequiredBedParams(searchParams: URLSearchParams): {
  market: string
  lang: string
  bed: string
  config: string
} {
  const market = searchParams.get('market')
  const lang = searchParams.get('lang')
  const bed = searchParams.get('bed')
  const config = searchParams.get('config')

  const missing = [
    !market && 'market',
    !lang && 'lang',
    !bed && 'bed',
    !config && 'config',
  ].filter(Boolean)

  if (missing.length > 0) {
    throw new Error(
      `Bed URL must include ${missing.join(', ')} query parameter(s). ` +
        'Copy the full share link from the configurator overview.',
    )
  }

  return { market: market!, lang: lang!, bed: bed!, config: config! }
}

function applyRenderParams(
  target: URLSearchParams,
  source: URLSearchParams,
  options?: ScreenshotUrlOptions,
): void {
  const camera =
    options?.camera ?? source.get('camera') ?? source.get('cam')
  if (camera) target.set('camera', camera)

  const shadows = options?.shadows ?? source.get('shadows')
  if (shadows) target.set('shadows', shadows)

  const showOnly =
    options?.showOnly ?? source.get('showOnly') ?? source.get('show_only')
  if (showOnly) target.set('showOnly', showOnly)
}

/**
 * Convert a configurator bed URL (`/?market=...&bed=...&config=...`) into the
 * dedicated screenshot entrypoint used by Frame API / Celery jobs.
 */
export function bedUrlToScreenshotUrl(
  bedUrl: string,
  options?: ScreenshotUrlOptions,
): string {
  const parsed = new URL(bedUrl)

  if (isScreenshotUrl(bedUrl)) {
    const params = new URLSearchParams(parsed.search)
    readRequiredBedParams(params)
    params.set('noUI', 'true')
    applyRenderParams(params, params, options)
    const origin = options?.baseOrigin ?? parsed.origin
    return `${origin}/screenshot?${params.toString()}`
  }

  const { market, lang, bed, config } = readRequiredBedParams(parsed.searchParams)

  const params = new URLSearchParams()
  params.set('market', market)
  params.set('lang', lang)
  params.set('bed', cleanBedSlug(bed))
  params.set('config', config)
  params.set('noUI', 'true')
  applyRenderParams(params, parsed.searchParams, options)

  const origin = options?.baseOrigin ?? parsed.origin
  return `${origin}/screenshot?${params.toString()}`
}

/** Frame API POST body for a screenshot render (matches Makefile frame-api-test-json). */
export function buildFrameApiRenderRequest(
  screenshotUrl: string,
  overrides?: Partial<Omit<FrameApiRenderRequest, 'url' | 'capture_mode'>>,
): FrameApiRenderRequest {
  return {
    url: screenshotUrl,
    width: overrides?.width ?? 3840,
    height: overrides?.height ?? 2160,
    wait_timeout_ms: overrides?.wait_timeout_ms ?? 180_000,
    device_scale_factor: overrides?.device_scale_factor ?? 1,
    capture_mode: 'screenshot',
  }
}
